import { useMutation } from '@apollo/client'
import type { Web3AuthCore } from '@web3auth/core'
import { ethers } from 'ethers'
import { useEffect, useState } from 'react'

import { gql } from '@/clients/utoniq-core'
import { useMessage } from '@/core'
import { useTranslation } from '@/core/i18n'

export const useWeb3authWalletConnect = ({
  loading,
  walletAddress,
  isArtist,
  refetch,
}: {
  loading: boolean
  walletAddress?: string | null
  isArtist: boolean
  refetch?: any
}) => {
  const { t } = useTranslation('wallet')
  const { showMessage, showError } = useMessage()

  const [web3auth, setWeb3auth] = useState<Web3AuthCore>()

  /**
   * user用ウォレット連携認証メッセージ発行・ウォレット連携
   */
  const [generateWalletSignatureMessage] = useMutation(
    GENERATE_WALLET_SIGNATURE_MESSAGE
  )
  const [authorizeWallet] = useMutation(AUTHORIZE_WALLET)

  /**
   * artist用ウォレット連携認証メッセージ発行・ウォレット連携
   */
  const [artistGenerateWalletSignatureMessage] = useMutation(
    ARTIST_GENERATE_WALLET_SIGNATURE_MESSAGE
  )
  const [artistAuthorizeWallet] = useMutation(ARTIST_AUTHORIZE_WALLET)

  /**
   * 認証メッセージによるウォレット認証・firestore userDocへのウォレットアドレス登録
   * @return true: 完了 error: 各種エラー
   */
  const userAuthorizeWalletBySignatureMessage = async () => {
    const web3auth = await import('.').then(m => m.web3auth)
    setWeb3auth(web3auth)
    if (!web3auth.provider) throw new Error('web3auth provider not found')

    const provider = new ethers.providers.Web3Provider(web3auth.provider)
    const signer = provider.getSigner()

    const account = await import('.').then(m => m.getWalletAddress())

    /**
     * 署名用メッセージ取得mutation（ユーザー用）
     */
    const { data } = await generateWalletSignatureMessage()
    const signatureMessage =
      data?.generateWalletSignatureMessage?.authToken?.signatureMessage

    if (!signatureMessage) throw new Error(t('signatureError'))

    /**
     * 署名用メッセージにて署名
     */
    const signature = await import('.').then(m =>
      m.signWalletBySignatureMessage(signer, signatureMessage)
    )

    /**
     * バックエンド側にてアドレス登録（ユーザー用）
     */
    await authorizeWallet({
      variables: { inputs: { publicAddress: account, signature } },
    })

    return true
  }

  /**
   * 認証メッセージによるウォレット認証・firestore artistDocへのウォレットアドレス登録
   * @return true: 完了 error: 各種エラー
   */
  const artistAuthorizeWalletBySignatureMessage = async () => {
    const web3auth = await import('.').then(m => m.web3auth)
    setWeb3auth(web3auth)
    if (!web3auth.provider) throw new Error('web3auth provider not found')

    const provider = new ethers.providers.Web3Provider(web3auth.provider)
    const signer = provider.getSigner()

    const account = await import('.').then(m => m.getWalletAddress())

    /**
     * 署名用メッセージ取得mutation（アーティスト用）
     */
    const { data } = await artistGenerateWalletSignatureMessage()

    const signatureMessage =
      data?.artistGenerateWalletSignatureMessage?.authToken?.signatureMessage

    if (!signatureMessage) throw new Error(t('signatureError'))

    /**
     * 署名用メッセージにて署名
     */
    const signature = await import('.').then(m =>
      m.signWalletBySignatureMessage(signer, signatureMessage)
    )

    /**
     * バックエンド側にてアドレス登録（アーティスト用）
     */
    await artistAuthorizeWallet({
      variables: { inputs: { publicAddress: account, signature } },
    })

    return true
  }

  const [web3authWalletLoading, setWeb3authWalletLoading] = useState(false)
  useEffect(() => {
    import('.').then(m => m.initWeb3auth())
    setWeb3auth(web3auth)
    const setCryptoWalletAddressFromWeb3auth = async () => {
      const web3auth = await import('.').then(m => m.web3auth)

      if (!web3auth.provider) return

      if (loading || walletAddress) return

      setWeb3authWalletLoading(true)
      try {
        isArtist
          ? await artistAuthorizeWalletBySignatureMessage()
          : await userAuthorizeWalletBySignatureMessage()
        refetch && (await refetch())

        showMessage(t('walletCreated'))
      } catch (e) {
        showError(e as Error)
      } finally {
        setWeb3authWalletLoading(false)
      }
    }

    setCryptoWalletAddressFromWeb3auth()
  }, [loading, walletAddress, web3auth?.provider])

  return {
    web3authWalletLoading,
  }
}

const GENERATE_WALLET_SIGNATURE_MESSAGE = gql`
  mutation GenerateWalletSignatureMessage {
    generateWalletSignatureMessage(inputs: { cryptoWalletType: web3auth }) {
      authToken {
        signatureMessage
      }
    }
  }
`

const ARTIST_GENERATE_WALLET_SIGNATURE_MESSAGE = gql`
  mutation GenerateWalletSignatureMessage {
    artistGenerateWalletSignatureMessage(
      inputs: { cryptoWalletType: web3auth }
    ) {
      authToken {
        signatureMessage
      }
    }
  }
`

const AUTHORIZE_WALLET = gql`
  mutation AuthorizeWallet($inputs: AuthorizeWalletInput!) {
    authorizeWallet(inputs: $inputs) {
      address
      authToken {
        nonce
        used
        createdAt
        expiredAt
      }
      connectedAt
      status
      walletType
    }
  }
`

const ARTIST_AUTHORIZE_WALLET = gql`
  mutation AuthorizeWallet($inputs: AuthorizeWalletInput!) {
    artistAuthorizeWallet(inputs: $inputs) {
      address
      authToken {
        nonce
        used
        createdAt
        expiredAt
      }
      connectedAt
      status
      walletType
    }
  }
`
